import { BrowserRouter, MemoryRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { supabase } from "./lib/supabase";
import CookieConsentBanner from "./components/feature/CookieConsentBanner";

const StorePage = lazy(() => import("./pages/store/page"));

// Known platform hostnames — never treat these as custom domains
const PLATFORM_HOSTS = ["localhost", "readdy.ai", "eventshowcase.de"];

function isPlatformHost(hostname: string): boolean {
  return (
    PLATFORM_HOSTS.some((h) => hostname === h || hostname.endsWith(`.${h}`)) ||
    hostname.endsWith(".readdy.ai") ||
    /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-14 h-14 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-500 text-sm font-medium">Loading store...</p>
      </div>
    </div>
  );
}

function CustomDomainGate({ children }: { children: React.ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [storeUserId, setStoreUserId] = useState<string | null>(null);

  useEffect(() => {
    const hostname = window.location.hostname;

    if (isPlatformHost(hostname)) {
      setChecking(false);
      return;
    }

    supabase
      .from("store_profiles")
      .select("user_id")
      .eq("custom_domain", hostname)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.user_id) {
          setStoreUserId(data.user_id);
        }
        setChecking(false);
      })
      .catch(() => setChecking(false));
  }, []);

  if (checking) {
    return <LoadingScreen />;
  }

  // Custom domain matched — render StorePage directly inside a MemoryRouter
  // so Link components inside StorePage still work without crashing
  if (storeUserId) {
    return (
      <MemoryRouter>
        <Suspense fallback={<LoadingScreen />}>
          <StorePage overrideUserId={storeUserId} />
        </Suspense>
      </MemoryRouter>
    );
  }

  return <>{children}</>;
}

// Handles Google OAuth callback: detects ?code= or #access_token and redirects user
function OAuthCallbackHandler() {
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;

    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const errorCode = url.searchParams.get('error_code') || hashParams.get('error_code');

    // If there's an error in the URL, just clean it up — don't redirect to error page
    if (errorCode) {
      window.history.replaceState({}, '', '/');
      return;
    }

    // Handle implicit flow (hash-based #access_token)
    if (accessToken && refreshToken) {
      handled.current = true;
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(async ({ data, error }) => {
          window.history.replaceState({}, '', '/');
          if (error || !data?.session) return;
          await handlePostAuth(data.session.user);
        });
      return;
    }

    // Handle PKCE flow (?code=) — only at root or when redirected back from /confirm-email
    // We handle ?code= at root OR if the current path is /confirm-email (shouldn't happen but safety net)
    if (code) {
      handled.current = true;
      supabase.auth.exchangeCodeForSession(code).then(async ({ data, error }) => {
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        url.searchParams.delete('provider_token');
        url.searchParams.delete('provider_refresh_token');
        const cleanPath = url.pathname === '/confirm-email' ? '/' : url.pathname;
        const cleanSearch = url.search === '?' ? '' : url.search;
        window.history.replaceState({}, '', cleanPath + cleanSearch);

        if (error || !data?.session) {
          console.error('OAuth exchange error:', error?.message);
          return;
        }
        await handlePostAuth(data.session.user);
      });
    }
  }, []);

  return null;
}

async function handlePostAuth(user: { id: string; email?: string; user_metadata?: Record<string, string> }) {
  const { data: existingProfile } = await supabase
    .from('profiles')
    .select('id, role')
    .eq('id', user.id)
    .maybeSingle();

  const navigate = (path: string) => {
    if (typeof window.REACT_APP_NAVIGATE === 'function') {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  if (!existingProfile) {
    const isAdmin = user.email?.toLowerCase() === 'cazeludegbunam@gmail.com';
    await supabase.from('profiles').insert([{
      id: user.id,
      email: user.email,
      full_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || '',
      role: isAdmin ? 'admin' : 'user',
      provider: 'google',
    }]);
    navigate(isAdmin ? '/admin' : '/dashboard');
  } else {
    // Update provider if not set
    if (!existingProfile.role) {
      await supabase.from('profiles').update({ provider: 'google' }).eq('id', user.id);
    }
    navigate(existingProfile.role === 'admin' ? '/admin' : '/dashboard');
  }
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <CustomDomainGate>
        <BrowserRouter basename={__BASE_PATH__}>
          <OAuthCallbackHandler />
          <AppRoutes />
          <CookieConsentBanner />
        </BrowserRouter>
      </CustomDomainGate>
    </I18nextProvider>
  );
}

export default App;
